import type { Dispatch, SetStateAction } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { QuestionInputField } from '/index';

import {
  ERR_TYPE_ENUM_IS_DROPDOWN,
  ERR_TYPE_NUM_LARGE,
  ERR_TYPE_NUM_SMALL,
  ERR_TYPE_REQUIRED,
  ERR_TYPE_STR_LONG,
  ERR_TYPE_STR_SHORT,
  FIELD_TYPE_DROPDOWN,
  FIELD_TYPE_NUMBER,
  FIELD_TYPE_TXTAREA,
} from '/business/shared/components/questions/helper/fieldEnums';

type FuelUnit =
  | string
  | number
  | {
      displayName?: string;
      displayNameKey?: string;
      name: string | number | null;
    };

export type FuelType = {
  displayName: string;
  name: string | null;
  units?: FuelUnit[];
};

type InputValue = {
  inputValue?: string | null;
};

type FuelTypeValue = {
  displayName?: string;
  displayNameKey: string;
  isPlaceholder?: boolean;
  name: string | null;
};

interface SharedFuelValuesOptions {
  fuelTypes: FuelType[];
  setValueFuelType: Dispatch<SetStateAction<string | null>>;
  valueFuelType: string | null;
}

const useGetSharedFuelValues = ({ fuelTypes, valueFuelType, setValueFuelType }: SharedFuelValuesOptions) => {
  const { t } = useTranslation();

  const fuelTypeValues = useMemo<FuelTypeValue[]>(() => {
    const placeholder: FuelTypeValue = {
      name: null,
      isPlaceholder: true,
      displayNameKey: 'business:QuestionPage.forms.otherFuels.selectFuel',
    };
    return [
      placeholder,
      ...fuelTypes.map(({ displayName, name }) => {
        return {
          name,
          displayName,
          displayNameKey: 'business:emissionFactors.buildingFuelTypes.'.concat(displayName),
        };
      }),
    ];
  }, [fuelTypes]);

  const fuelType = useMemo(() => {
    return {
      labelI18nKey: 'business:QuestionPage.forms.otherFuels.para3',
      type: FIELD_TYPE_DROPDOWN,
      values: fuelTypeValues,
      styleInputMaxWidth: '22rem',
      saveMeCallback: (value: InputValue) => {
        const { inputValue: fuelTypeSlug } = value;
        return {
          fuelType: fuelTypes.find(({ name }) => name === fuelTypeSlug),
          hasFuelType: true,
        };
      },
      onChangeCallback: (value: string | null) => {
        setValueFuelType(value);
      },
      element: 'fuel_type',
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOFUELTYPE',
          errStr: t('business:Errors.fuelType.provideType'),
        },
        {
          rule: ERR_TYPE_ENUM_IS_DROPDOWN,
          errCode: 'ERRFUELTYPENOTFOUND',
          errStr: t('business:Errors.fuelType.notFound'),
        },
      ],
    };
  }, [setValueFuelType, fuelTypes, fuelTypeValues, t]);

  const quantity = useMemo(() => {
    const fuelTypesUnits = fuelTypes.map((item) => {
      const typeUnitMapped = item?.units?.map((typeUnit) => {
        if (typeof typeUnit === 'string' || typeof typeUnit === 'number') {
          const unitName = String(typeUnit);
          return {
            name: unitName,
            displayName: unitName,
            displayNameKey: 'business:emissionFactors.volumes.'.concat(unitName),
          };
        }
        return {
          ...typeUnit,
          name: typeUnit.name === null ? null : String(typeUnit.name),
        };
      });
      return {
        ...item,
        units: typeUnitMapped,
      };
    });
    return {
      element: 'quantity',
      propName: 'quantity',
      type: FIELD_TYPE_NUMBER,
      styleInputMaxWidth: '20rem',
      dropdown: {
        values: fuelTypesUnits?.find(({ name }) => name === valueFuelType)?.units,
        propName: 'fuelUnit',
        element: 'fuel_unit',
      },
      label: 'Quantity:',
      placeholderI18nKey: 'business:QuestionPage.forms.otherFuels.quantityPlaceholder',
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOQUANTITY',
          errStr: t('business:Errors.fuelType.fuelQuantity'),
        },
        {
          rule: ERR_TYPE_NUM_SMALL,
          errCode: 'ERRQUANTITYTOOLOW',
          errStr: t('business:Errors.fuelType.fuelQuantity'),
          value: 0,
        },
        {
          rule: ERR_TYPE_NUM_LARGE,
          errCode: 'ERRQUANTITYTOOHIGH',
          errStr: t('business:Errors.fuelType.fuelQuantity'),
          value: 9999999,
        },
      ],
    };
  }, [fuelTypes, valueFuelType, t]);

  const dataSource = useMemo(() => {
    return {
      element: 'data_source',
      type: FIELD_TYPE_TXTAREA,
      propName: 'dataSource',
      labelI18nKey: 'business:QuestionPage.forms.otherFuels.para5',
      descriptionI18nKey: 'business:QuestionPage.forms.otherFuels.para6',
      placeholderI18nKey: 'business:QuestionPage.forms.otherFuels.dataPlaceholder',
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRDATASOURCE',
          errStr: t('business:Errors.dataSource.provide'),
        },
        {
          rule: ERR_TYPE_STR_SHORT,
          errCode: 'ERRDATASOURCETOOSHORT',
          errStr: t('business:Errors.dataSource.tooShort'),
          value: 5,
        },
        {
          rule: ERR_TYPE_STR_LONG,
          errCode: 'ERRDATASOURCETOOLONG',
          errStr: t('business:Errors.dataSource.tooLong'),
          value: 3000,
        },
      ],
    };
  }, [t]);

  const pageSchema = useMemo<QuestionInputField[]>(
    () => [fuelType, quantity, dataSource],
    [fuelType, quantity, dataSource],
  );
  return {
    fuelTypeValues,
    pageSchema,
  };
};

export default useGetSharedFuelValues;
